import * as $ from 'jquery';

import { config, ConfigOptions } from './config';
import { Sandbox, Hook } from './Sandbox';
import { Module } from './Module';
import { Connector } from './Connector';

import { toCamel } from './utils/string';
import { mergeRegistries } from './utils/extend';
import { readStateFromHash } from 'lib/util/url';

interface Connectors {
	[name : string] : Connector
}

export interface ConnectorRegistry {
	[name : string] : typeof Connector
}

export interface Skin<M extends Module = Module, S extends M = M> {
	(this : S, parent : M) : void
}

export interface SkinRegistry {
	[skinName : string] : Skin<any, any>
}

export interface RegistryEntry extends SimplifiedRegistryEntry {
	skins : SkinRegistry
}

export interface Registry extends SimplifiedRegistry {
	[name : string] : RegistryEntry
}

export interface SimplifiedRegistryEntry {
	module : typeof Module;
	skins? : SkinRegistry
}

export interface SimplifiedRegistry {
	[name : string] : SimplifiedRegistryEntry | typeof Module
}

export interface IDList {
	[modName : string] : number
}

export function transformRegistryToFull(registry : SimplifiedRegistry) : Registry {
	const result : Registry = {};
	for(const moduleName in registry) {
		const definitionOrModule = registry[moduleName];
		if(typeof definitionOrModule === 'function') {
			result[moduleName] = {
				module: definitionOrModule,
				skins: {},
			};
		} else {
			result[moduleName] = {
				module: definitionOrModule.module,
				skins: definitionOrModule.skins || {},
			};
		}
	}

	return result;
}

/**
 * Responsible for application-wide issues such as the creation of modules and establishing connections between them.
 *
 * @author Remo Brunschwiler
 * @namespace Tc
 * @class Application
 */
export class Application {
	/**
	 * The module registry.
	 * In legacy, the modules are registered on the window.Tc.Module namespace.
	 */
	public registry : Registry;

	/**
	 * The connector registry.
	 * Usually the modules are registered on the window.Tc.Connector namespace
	 */
	public connectorTypes : ConnectorRegistry;

	/**
	 * The configuration.
	 *
	 * @property config
	 * @type Object
	 */
	public config : ConfigOptions;

	/**
	 * The jQuery context.
	 *
	 * @property $ctx
	 * @type jQuery
	 */
	public $ctx : JQuery;

	/**
	 * Contains references to all modules on the page. This can, for
	 * example, be useful when there are interactions between Flash
	 * objects and Javascript.
	 *
	 * @property modules
	 * @type Array
	 */
	public modules : Module[] = [];

	/**
	 * Associates modules that are hash-capable with their IDs.
	 */
	public readonly hashCapableModules : {[moduleId : number] : Module | undefined} = {};

	/**
	 * Contains references to all connectors on the page.
	 *
	 * @property connectors
	 * @type Object
	 */
	public connectors : Connectors = {};

	/**
	 * The sandbox to get the resources from.
	 * This sandbox is shared between all modules.
	 *
	 * @property sandbox
	 * @type Sandbox
	 */
	public sandbox : Sandbox;

	/**
	 * Initializes the application.
	 *
	 * @method init
	 * @constructor
	 * @param {jQuery} $ctx
	 *      The jQuery context
	 * @param {Object} config
	 *      The configuration
	 */
	constructor(
		$ctx : JQuery,
		cfg : Partial<ConfigOptions> = {},
		registry : SimplifiedRegistry = {},
		connectorTypes : ConnectorRegistry = {},
	) {
		this.config = {...config, ...cfg};
		this.$ctx = $ctx || $('body');
		this.connectors = {};
		this.sandbox = new Sandbox(this, this.config);
		this.registry = {...transformRegistryToFull(registry)};
		this.connectorTypes = {...connectorTypes};
	}

	/**
	 * Allows to amend the registry of known modules.
	 * @param registry The registry to merge into the current one
	 */
	// Modified by Swisscom (Raphael Schweikert, taascraa): add ability to register modules after initialization
	public amendRegistry(registry : SimplifiedRegistry) : void {
		mergeRegistries(this.registry, transformRegistryToFull(registry));
	}

	/**
	 * Register modules withing scope
	 * Automatically registers all modules within the scope,
	 * as long as the modules use the OOCSS naming conventions.
	 *
	 * @method registerModules
	 * @param {jQuery} $ctx
	 *      The jQuery context
	 * @return {Array}
	 *      A list containing the references of the registered modules
	 */
	// Modified by Swisscom (Raphael Schweikert, taascraa): add ability to have multiple modules on a single element
	public registerModules($ctx : JQuery = this.$ctx) : Module[] {
		const modules : Module[] = [];

		// Modified by Swisscom (Dave Talamona, tzhtada2):
		// [1. performance improvement by excluding the NOT from the initial query]
		// [2. descendants of a container with attribute "data-ignore-descendants" are not initialized]
		const $candidates = $ctx.find('.mod')
			.add($ctx.hasClass('mod') ? $ctx : $())
			.not('[data-ignore="true"], [data-ignore-descendants="true"] *');

		$candidates.each((_, elem) : void => {
			const $elem = $(elem);
			// Modified by Swisscom (Dave Talamona, tzhtada2):
			// Modules with newlines or tabs in class attribute were not detected as a modules (classes.length was 1)
			const classes = $.trim($elem.attr('class') || '').split(/\s+/);

			/*
			 * A module can have several different classes and data attributes.
			 * See below for possible values.
			 */

			/*
			 * @config .mod
			 *
			 * Indicates that it is a base module, this is the default and
			 * no JavaScript needs to be involved. It must occur excactly
			 * once.
			 */

			/*
			 * @config .mod{moduleName} || .mod-{module-name}
			 *
			 * Indicates that it is a module of type basic, which is
			 * derived from the base module. It can occur at most
			 * once. Example: .modBasic || .mod-basic
			 */

			/*
			 * @config .skin{moduleName}{skinName} || .skin-{module-name}-{skin-name}
			 *
			 * Indicates that the module basic has the submarine skin. It
			 * will be decorated by the skin JS (if it exists). It can occur
			 * arbitrarily. Example: .skinBasicSubmarine || .skin-basic-submarine
			 */

			/*
			 * @config data-connectors
			 *
			 * A module can have a comma-separated list of data connectors.
			 * The list contains the IDs of the connectors in the following
			 * schema: {connectorType}-{connectorId}
			 *
			 * {connectorType} is optional. If only the {connectorId} is given, the
			 * default connector is instantiated.
			 *
			 * The example MasterSlave-Navigation decodes to: type =
			 * MasterSlave, id = Navigation. This instantiates the MasterSlave
			 * connector (as mediator) with the connector id Navigation.
			 * The connector id is used to chain the appropriate (the ones with the same id)
			 * modules together and to improve the reusability of the connector.
			 * It can contain multiple connector ids (e.g. 1,2,MasterSlave-Navigation).
			 */
			if(classes.length <= 1) {
				return;
			}

			/*
			 * This needs to be done via attr() instead of data().
			 * As data() cast a single number-only connector to an integer, the split will fail.
			 */
			const connectorParts = $.trim($elem.attr('data-connectors') || '').split(/\s*,\s*/);

			// Do nothing for empty connectors
			const connectors = connectorParts.filter(Boolean);

			const classesNormalized = classes
				.filter(Boolean)
				.map(part => {
					if (part.indexOf('-') > -1) {
						part = toCamel(part);
					}

					return part;
				});

			const modNames = classesNormalized
				.filter(part => part.indexOf('mod') === 0 && part.length > 3)
				.map(part => part.substr(3))
				.filter(modName => modName in this.registry);

			for(const modName of modNames) {
				const definition = this.registry[modName];
				const skinRegistry = definition.skins;
				// Get skins for module
				const skins = classesNormalized
					.filter(part => {
						return part.indexOf(`skin${modName}`) === 0;
					})
					.map(skinClass => skinClass.substr('skin'.length + modName.length))
					.map(skinName => skinRegistry[skinName])
					.filter(Boolean);

				const module = this.registerModule($elem, definition.module, modName, skins, connectors);
				if(module) {
					modules.push(module);
				}
			}
		});

		return modules;
	}

	/**
	 * Registers a module.
	 *
	 * @method registerModule
	 * @param {jQuery} $node
	 *      The module node
	 * @param {String} modName
	 *      The module name. It must match the class name of the module
	 * @param {Array} skins
	 *      A list of skin names. Each entry must match a class name of a skin
	 * @param {Array} connectors
	 *      A list of connectors identifiers (e.g. MasterSlave-Navigation)
	 *      Schema: {connectorName}-{connectorId}
	 * @return {Module}
	 *      The reference to the registered module
	 */
	public registerModule<T extends Module>(
		$node : JQuery,
		module : typeof Module, // Actually typeof T but that would need an extra definition
		modName : string,
		skins? : Skin<Module, Module>[],
		connectors? : string[],
	) : T | null {
		const modules = this.modules;

		skins = skins || [];
		connectors = connectors || [];

		// Generate a unique ID for every module
		const id = modules.length;
		// Modified by Raphael Schweikert (taascraa): add ability to have multiple modules on a single element
		const ids : IDList = $node.data('terrific-ids') || {};
		$node.data('terrific-ids', ids);
		// Modified by Dave Talamona (tzhtada2): modules must not be initialized twice
		if(modName in ids) {
				return null;
		}
		ids[modName] = id;

		// Instantiate module
		modules[id] = new module($node, this.sandbox, id, modName);
		// Added by Swisscom (Raphael Schweikert, taascraa)
		modules[id].modName = modName;

		// Decorate it
		for(const skin of skins) {
			modules[id] = modules[id].getDecoratedModule(skin);
		}

		// Register connections
		for (let i = 0, len = (connectors || []).length; i < len; i++) {
			this.registerConnection(connectors[i], modules[id]);
		}

		return modules[id] as T;
	}

	/**
	 * Registers a connection between a module and a connector.
	 *
	 * @method registerConnection
	 * @param {String} connector
	 *      The full connector name (e.g. MasterSlave-Navigation)
	 * @param {Module} component
	 *      The module instance
	 */
	public registerConnection(connector : string, component : Module) : void {
		connector = $.trim(connector);

		const parts = connector.split('-');
		let connectorType : string | undefined;
		let connectorId : string;
		let identifier : string | undefined;

		if (parts.length === 1) {
				// Default connector
				identifier = connectorId = parts[0];
		} else if (parts.length === 2) {
				// A specific connector type is given
				connectorType = parts[0];
				connectorId = parts[1];
				identifier = connectorType + connectorId;
		} else {
			return;
		}

		if (!identifier) {
			return;
		}

		const connectors = this.connectors;

		if (!connectors[identifier]) {
			const registry = this.connectorTypes;

			let ctor = Connector;
			if(connectorType && connectorType in registry) {
				ctor = registry[connectorType];
			}
			connectors[identifier] = new ctor(connectorId);
		}

		if(!(identifier in connectors)) {
			return;
		}

		/*
		 * The connector observes the component and attaches it as
		 * an observer.
		 */
		component.attachConnector(connectors[identifier]);

		/*
		 * The component wants to be informed over state changes.
		 * It registers it as connector member.
		 */
		connectors[identifier].registerComponent(component);
	}

	/**
	 * Unregisters the modules given by the module instances.
	 *
	 * @method unregisterModules
	 * @param {Array} modules
	 *      A list containing the module instances to unregister
	 */
	public unregisterModules(modules : Module[] = this.modules) {
		const connectors = this.connectors;

		// Unregister the given modules
		for(const module of modules) {
			// Delete the references in the connectors
			for (const connectorId in connectors) {
				if (connectors.hasOwnProperty(connectorId)) {
					connectors[connectorId].unregisterComponent(module);
				}
			}

			// Delete the module instance itself
			const index = $.inArray(module, this.modules);
			if (index > -1) {
				try {
					// Modified by Swisscom (Dave Talamona, tzhtada2):
					// Clear module id when unregistering it so that it can be initialized again
					const ids : IDList = module.$ctx.data('terrific-ids') || {};
					// tslint:disable-next-line:no-dynamic-delete
					delete ids[module.modName];
				} catch (e) {}
				// tslint:disable-next-line:no-dynamic-delete
				delete this.modules[index];
				if(module.id in this.hashCapableModules) {
					this.hashCapableModules[module.id] = undefined;
				}
			}
		}
	}

	/**
	 * Registers a hook that is called at the end.
	 *
	 * @method end
	 * @param {Function} hook
	 * 		The hook function to be executed
	 */
	public end(hook : Hook) {
		this.sandbox.addCallback('end', hook);
	}

	/**
	 * Starts (intializes) the registered modules.
	 *
	 * @method start
	 * @param {Array} modules
	 *      A list of the modules to start
	 */
	public start(modules : Module[] = this.modules) {
		// Start the modules
		for(const module of modules) {
			module.start();
			this.initHashHandlingForModule(module);
		}

		this.initHashHandling();
	}

	/**
	 * Stops the registered modules.
	 *
	 * @method stop
	 * @param {Array} modules
	 *      A list containing the module instances to stop
	 */
	public stop(modules : Module[] = this.modules) {
		// Stop the modules
		for(const module of modules) {
			module.stop();
		}
	}

	/**
	 * Unregisters a module from a connector.
	 *
	 * @method unregisterConnection
	 * @param {String} connectorId
	 *      The connector channel id (e.g. 2)
	 * @param {Module} component
	 *      The module instance
	 */
	public unregisterConnection(connectorId : string, component : Module) {
		const connector = this.connectors[connectorId];

		// Delete the references in the connector and the module
		if (connector) {
			connector.unregisterComponent(component);
			component.detachConnector(connector);
		}
	}

	private initHashHandlingForModule(module : Module) {
		if(!module.moduleStateChanged) {
			return;
		}
		const hashKeyConfigured = module.hashKey;
		// Make hashKey unique
		let hashKey = hashKeyConfigured;
		let index = 0;
		let existing : string | undefined;
		do {
			existing = Object.keys(this.hashCapableModules)
				.map(id => this.hashCapableModules[Number(id)])
				.filter(Boolean)
				.map(mod => mod!.hashKey)
				.find(key => key === hashKey);

			if(existing) {
				index++;
				hashKey = hashKeyConfigured + index;
			}
		} while (existing);
		module.hashKey = hashKey;
		this.hashCapableModules[module.id] = module;
	}

	private setModuleStatesFromHash() {
		for(const moduleId in this.hashCapableModules) {
			const module = this.hashCapableModules[moduleId];
			if(!module) {
				continue;
			}
			const hashKey = module.hashKey;
			const state = readStateFromHash(hashKey);
			if(state !== undefined) {
				module.moduleStateChanged!(state);
			}
		}
	}

	private initHashHandling() {
		if(!Object.keys(this.hashCapableModules).length) {
			return;
		}
		$(window).on('hashchange', this.setModuleStatesFromHash.bind(this));
		this.setModuleStatesFromHash();
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/vendor/terrific/Application.ts